import { createTheme, type PaletteOptions } from '@mui/material/styles';
import { darkPalette, lightPalette, toCssVars, type Palette } from './palettes';
import { FONT_BODY, tokens } from './tokens';

// CSS 変数テーマ(cssVariables + colorSchemes)を使うことを MUI の型に伝える。
// これがないと Theme 型に colorSchemes / getColorSchemeSelector などが現れない。
declare module '@mui/material/styles' {
  interface CssThemeVariables {
    enabled: true;
  }
}

/** MUI の palette は色の計算(明暗の派生色など)に使うため、var() ではなく実際の色を渡す。 */
const muiPalette = (p: Palette): PaletteOptions => ({
  background: { default: p.bg, paper: p.panel },
  text: { primary: p.tx, secondary: p.mu, disabled: p.dim },
  primary: { main: p.accent },
  secondary: { main: p.blue },
  error: { main: p.red },
  warning: { main: p.dest.G },
  success: { main: p.ok },
  divider: p.line,
});

export const theme = createTheme({
  // <html data-mui-color-scheme="light|dark"> で切り替える。ライトを既定とする(AppProviders の defaultMode)
  cssVariables: { colorSchemeSelector: '[data-mui-color-scheme="%s"]' },
  colorSchemes: {
    light: { palette: muiPalette(lightPalette) },
    dark: { palette: muiPalette(darkPalette) },
  },
  breakpoints: { values: { xs: 0, sm: 760, md: 1100, lg: 1536, xl: 1920 } },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: FONT_BODY,
    fontSize: 14,
    h1: { fontSize: 22, fontWeight: 700, letterSpacing: '0.02em', lineHeight: 1.4 },
    h2: { fontSize: 15, fontWeight: 700, lineHeight: 1.5 },
    h3: { fontSize: 13, fontWeight: 400, lineHeight: 1.5 },
    body1: { fontSize: 14, lineHeight: 1.6 },
    body2: { fontSize: 13, lineHeight: 1.6 },
    button: { textTransform: 'none', fontSize: 13 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // --kdk-* 変数。属性がなければライト。dark のときだけ上書きする(html[...] は :root より詳細度が高い)
        ':root': toCssVars(lightPalette),
        'html[data-mui-color-scheme="dark"]': toCssVars(darkPalette),
        'html, body, #root': { height: '100%' },
        body: { WebkitFontSmoothing: 'antialiased' },
        ':focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': { transition: 'none !important', animation: 'none !important' },
        },
      },
    },
    MuiButton: { defaultProps: { disableElevation: true } },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: tokens.line },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${tokens.line}`, padding: '8px 10px' },
        head: {
          backgroundColor: tokens.panel2,
          color: tokens.mu,
          fontSize: 12.5,
          fontWeight: 400,
          whiteSpace: 'nowrap',
        },
      },
    },
  },
});
